import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import PrivacyPolicyScreen from './screens/PrivacyPolicyScreen';
import TermsOfServiceScreen from './screens/TermsOfServiceScreen';
import RefundPolicyScreen from './screens/RefundPolicyScreen';


// --- LAZY LOADED ROUTES FOR CODE SPLITTING ---
const HomeScreen = lazy(() => import('./screens/HomeScreen'));
const ProductScreen = lazy(() => import('./screens/ProductScreen'));
const PlaceOrderScreen = lazy(() => import('./screens/PlaceOrderScreen'));
const CartScreen = lazy(() => import('./screens/CartScreen'));
const MyOrdersScreen = lazy(() => import('./screens/MyOrdersScreen'));
const ShippingScreen = lazy(() => import('./screens/ShippingScreen'));
const PaymentScreen = lazy(() => import('./screens/PaymentScreen'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const SettingsScreen = lazy(() => import('./screens/SettingsScreen'));
const OurStory = lazy(() => import('./pages/OurStory'));
const OrderDetailsScreen = lazy(() => import('./screens/OrderDetailsScreen'));
const ProfileScreen = lazy(() => import('./screens/ProfileScreen'));
const ContactScreen = lazy(() => import('./screens/ContactScreen'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const CategoryScreen = lazy(() => import('./screens/CategoryScreen'));
const ForgotPasswordScreen = lazy(() => import('./screens/ForgotPasswordScreen'));
const NotFoundScreen = lazy(() => import('./screens/NotFoundScreen'));
const ServerErrorScreen = lazy(() => import('./screens/ServerErrorScreen'));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function App() {
  const [isDarkMode, setIsDarkMode] = useState(localStorage.getItem('theme') === 'dark');

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark-mode');
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('theme', newMode ? 'dark' : 'light');
  };

  return (
    <Router>
      <ScrollToTop />
      <style>
        {`
          :root {
            --bg-base: #fdfdfd;
            --bg-blueprint: #f9f9f9;
            --bg-surface: #fff;
            --text-main: #000;
            --text-muted: #888;
            --text-subtle: #555;
            --border-main: #000;
            --border-light: #eee;
            --grid-line: #e8e8e8;
            --accent-red: #ff0000;
            --shadow-main: #000;
            --nav-bg: #000;
            --nav-text: #fff;
            --btn-bg: #000;
            --btn-text: #fff;
          }
          
          html.dark-mode {
            --bg-base: #111;
            --bg-blueprint: #1a1a1a;
            --bg-surface: #0a0a0a;
            --text-main: #fdfdfd;
            --text-muted: #aaa;
            --text-subtle: #ccc;
            --border-main: #fdfdfd;
            --border-light: #333;
            --grid-line: #333;
            --accent-red: #ff4444;
            --shadow-main: #fdfdfd;
            --nav-bg: #000;
            --nav-text: #fdfdfd;
            --btn-bg: #fdfdfd;
            --btn-text: #000;
          }

          body {
            background-color: var(--bg-base);
            color: var(--text-main);
            transition: background-color 0.3s ease, color 0.3s ease;
          }

          .grayscale-img {
            filter: grayscale(100%);
          }
          
          /* --- GLOBAL UI POLISH --- */
          ::selection { background: var(--text-main); color: var(--bg-surface); }
          ::-webkit-scrollbar { width: 10px; }
          ::-webkit-scrollbar-track { background: var(--bg-base); border-left: 1px solid var(--border-light); }
          ::-webkit-scrollbar-thumb { background: var(--border-main); }
          
          /* Brutalist Input Focus & Button Hovers */
          .form-control:focus, .form-select:focus {
            border-color: var(--border-main) !important;
            box-shadow: 4px 4px 0px var(--shadow-main) !important;
            outline: none !important;
            background-color: var(--bg-surface) !important;
            color: var(--text-main) !important;
          }
          .btn-dark, .btn-outline-dark, .btn-place-order, .black-btn-style, .add-to-cart-btn {
            transition: all 0.2s ease-in-out !important;
          }
          .btn-dark:hover, .btn-outline-dark:hover, .btn-place-order:hover, .black-btn-style:hover, .add-to-cart-btn:hover {
            transform: translate(-2px, -2px) !important;
            box-shadow: 4px 4px 0px var(--shadow-main) !important;
          }
          
          /* Global Brutalist Buttons */
          .btn-brutalist { transition: 0.1s; background-color: transparent; color: var(--text-main); border: 2px solid var(--border-main); }
          .btn-brutalist:hover:not(:disabled) { box-shadow: 6px 6px 0px var(--shadow-main); transform: translate(-2px, -2px); background-color: var(--text-main); color: var(--bg-surface); }
          .btn-brutalist:active:not(:disabled) { box-shadow: 2px 2px 0px var(--shadow-main); transform: translate(0px, 0px); }
          
          .btn-brutalist-dark { transition: 0.1s; background-color: var(--btn-bg); color: var(--btn-text); border: 2px solid var(--border-main); }
          .btn-brutalist-dark:hover:not(:disabled) { box-shadow: 6px 6px 0px var(--shadow-main); transform: translate(-2px, -2px); background-color: var(--btn-text); color: var(--btn-bg); }
          .btn-brutalist-dark:active:not(:disabled) { box-shadow: 2px 2px 0px var(--shadow-main); transform: translate(0px, 0px); }
          
          /* Fix Chrome Autofill in Dark Mode */
          input:-webkit-autofill,
          input:-webkit-autofill:hover, 
          input:-webkit-autofill:focus, 
          input:-webkit-autofill:active {
              -webkit-box-shadow: 0 0 0 30px var(--bg-surface) inset !important;
              -webkit-text-fill-color: var(--text-main) !important;
              transition: background-color 5000s ease-in-out 0s;
          }
          
          /* --- SMOOTH PAGE ENTRANCE --- */
          @keyframes pageFadeIn {
            0% { opacity: 0; transform: translateY(15px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          main > div, main > section, .container {
            animation: pageFadeIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }

          /* Cool Streetwear Noise Texture Overlay */
          body::after {
            content: ""; position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
            background-image: url('data:image/svg+xml;utf8,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.85%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22/%3E%3C/svg%3E');
            opacity: 0.04; pointer-events: none; z-index: 9999;
          }

          /* Floating Theme Toggle */
          .theme-toggle-btn {
            position: fixed;
            bottom: 30px;
            right: 30px;
            z-index: 10000;
            background-color: var(--bg-surface);
            color: var(--text-main);
            border: 2px solid var(--border-main);
            padding: 8px 12px;
            font-weight: 900;
            letter-spacing: 1px;
            font-size: 10px;
            box-shadow: 4px 4px 0px var(--shadow-main);
            transition: 0.2s ease-in-out;
          display: flex;
          align-items: center;
          gap: 8px;
          }
          .theme-toggle-btn:hover {
            transform: translate(-2px, -2px);
            box-shadow: 6px 6px 0px var(--shadow-main);
          }

          /* --- RESPONSIVE MOBILE OPTIMIZATIONS --- */
          @media (max-width: 768px) {
            /* Typography Scaling */
            h1 { font-size: clamp(2rem, 8vw, 3.5rem) !important; }
            h2 { font-size: clamp(1.5rem, 6vw, 2.5rem) !important; }
            
            /* Navbar Mobile View Fix */
            .navbar-brand { font-size: 1.5rem !important; font-weight: 900 !important; }
            .navbar-toggler { border: 2px solid var(--border-main) !important; border-radius: 0 !important; padding: 5px 10px !important; }
            .navbar-collapse { 
              background-color: var(--bg-surface); border: 2px solid var(--border-main); padding: 15px; 
              position: absolute; top: 100%; left: 5%; width: 90%; z-index: 1050; 
              box-shadow: 4px 4px 0px var(--shadow-main); text-align: center;
            }
            .navbar-nav .nav-link { font-size: 14px !important; font-weight: 800 !important; margin-bottom: 10px; border-bottom: 1px dashed var(--border-light); }
            
            /* Move theme toggle button lower in mobile view */
            .theme-toggle-btn {
          bottom: 20px;
              right: 20px;
          padding: 8px 12px; /* Smaller square padding for mobile icon */
          font-size: 16px; /* Adjusted icon size */
            }
        .theme-text { display: none; }
          }
        `}
      </style>
      
      <Toaster 
        position="bottom-right" 
        toastOptions={{ 
          style: { borderRadius: '0', background: 'var(--bg-surface)', color: 'var(--text-main)', border: '2px solid var(--border-main)', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '12px' },
          success: { iconTheme: { primary: 'var(--text-main)', secondary: 'var(--bg-surface)' } },
          error: { iconTheme: { primary: 'var(--accent-red)', secondary: 'var(--bg-surface)' } }
        }} 
      />
      <Navbar /> 
      <main style={{ minHeight: '85vh', backgroundColor: 'var(--bg-base)' }}>
        <Suspense fallback={<div style={{ height: '85vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--bg-surface)', color: 'var(--text-main)', fontWeight: '900', letterSpacing: '5px', textTransform: 'uppercase' }}>INITIALIZING SYSTEM...</div>}>
          <Routes>
            <Route path="/" element={<HomeScreen />} />
            <Route path="/collection/:categoryName" element={<CategoryScreen />} />
            <Route path="/product/:id" element={<ProductScreen />} />
            <Route path="/cart" element={<CartScreen />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
            <Route path="/shipping" element={<ShippingScreen />} />
            <Route path='/settings' element={<SettingsScreen />} />
            <Route path="/payment" element={<PaymentScreen />} />
            <Route path="/placeorder" element={<PlaceOrderScreen />} />
            <Route path="/myorders" element={<MyOrdersScreen />} />
            <Route path='/about' element={<AboutUs />} />
            <Route path='/profile' element={<ProfileScreen />} />
            <Route path='/our-story' element={<OurStory />} />
            <Route path='/contact' element={<ContactScreen />} />
            <Route path='/order/:id' element={<OrderDetailsScreen />} />
            <Route path='/500' element={<ServerErrorScreen />} />
            <Route path='*' element={<NotFoundScreen />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyScreen />} />
            <Route path="/terms-of-service" element={<TermsOfServiceScreen />} />
            <Route path="/refund-policy" element={<RefundPolicyScreen />} />

          </Routes>
        </Suspense>
      </main>

      <Footer />

      {/* GLOBAL THEME TOGGLE BUTTON */}
      <button className="theme-toggle-btn" onClick={toggleTheme}>
        <span className="theme-icon">{isDarkMode ? '☼' : '☾'}</span>
        <span className="theme-text">{isDarkMode ? 'LIGHT MODE' : 'DARK MODE'}</span>
      </button>
    </Router>
  );
}

export default App;